/**
 * Family by Generation & Age — the dashboard detail page for the tenth control.
 *
 * A plain five-column report, one row per member:
 *
 *     Gen | Age | Age at Death | Desc Name | Line Color
 *
 * The count sits beside the heading, the way every other detail page prints its
 * count. Every column carries a title above it — centred over the numbers,
 * flush left over the text columns — and each one is a `max-content` grid
 * track with a 6ch column-gap, so the space after the longest entry in a column
 * is exactly six spaces and no wider — see `.desc-age-gen-list` in App.css.
 *
 * The rows and the count are the report endpoint's own —
 * /report-management/family-in-age-order — which lists living descendants with
 * the generation, age, name and line colour already on each row, in the order
 * the report reads, and states its own total. The page prints exactly that, so
 * its number is the dashboard card's number. /dashboard-statistics-with-details
 * is read for what the report endpoint does not name: who heads each family
 * line, and the name markers — the in-law asterisk, the deceased angel and the
 * multiple-birth tag — which come off each row's roster record, the way Total
 * Family Members prints them. The roster's date of birth also sets the age
 * printed for a living member: their age today, so it moves up on the
 * birthday itself. The endpoint's own age stands wherever the roster has no
 * date.
 * Age at Death is filled for the deceased only — the age at death the form
 * stored on the roster record, else the years between the roster's dates of
 * birth and death — and stays blank on a living member's row. A member who
 * died before their first birthday shows the full months instead ("5 mos."),
 * or the days within the first month ("12 days"), counted from the roster's
 * dates; a death on the day of birth keeps "0".
 *
 * The filter bar is the shared report one — sorts on the left, member tabs on
 * the right — with Gen as this page's own leading sort. It opens oldest to
 * youngest with nothing lit, so Oldest to Youngest is grayed out until another
 * sort is picked. Once a generation is applied in the Generation dropdown it
 * opens by generation, then line colour, then oldest to youngest instead.
 * The tabs read each row's roster record, since the report rows carry no
 * status fields. See docs/dashboard-filter-rules.md.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import {
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardMemberNameLabel,
  DashboardNameMarkerGroup,
  DashboardPhotoModal,
  DashboardPhotoName,
  getDashboardMemberNamePhotoUrls,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import { fetchFamilyInAgeOrder } from "../../../Utils/dashboardDescendants";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";
import {
  DASHBOARD_MEMBER_FILTERS,
  DASHBOARD_REPORT_MEMBER_FILTER_BAR_PROPS,
  applyDashboardMemberFilterToggle,
  filterDashboardMembersByMemberFilter,
  getDashboardBaseSortForMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";
import {
  FilterTriggerLabel,
  filterTriggerClass,
  formatFilterValueList,
} from "../../../Utils/filterTriggerLabel";
import {
  getMemberAgeAtDeathLabel,
  getMemberAgeLabel,
  getMemberCurrentAge,
  getMemberDeathTimestamp,
  getMemberDobTimestamp,
} from "../../../Utils/family-tree/dateAndTime";
import {
  isDashboardDeceasedMember,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

/**
 * The order the report opens in: oldest to youngest across the whole family,
 * with nothing lit — so Oldest to Youngest is grayed out until another sort
 * takes over, as on Total Family Members.
 */
const DEFAULT_SORT = "oldest";

/**
 * The order the report opens in once a generation is picked in the Generation
 * dropdown: by generation, then line colour, then oldest to youngest inside
 * each colour — no sort button is lit for it.
 */
const GENERATION_DEFAULT_SORT = "gen-color-age";

/** Printed where the payload names no generation or no age. */
const MISSING_VALUE = "—";

/**
 * Colour + hex, normalised the way the legend builder normalises them, so a row
 * and the legend entry for the same family line land on the same key.
 */
const familyLineKey = (colorName, hexCode) =>
  `${String(colorName || "")
    .trim()
    .toLowerCase()
    .replace(/\s+line$/i, "")}|${String(hexCode || "").toLowerCase()}`;

/** Unknown numbers sort last whichever way the column is read. */
const compareNumbers = (a, b, { descending = false } = {}) => {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  return descending ? b - a : a - b;
};

function DescInAgeGeneration() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } =
    useDashboardPhotoModal();
  const [sortBy, setSortBy] = useState("none");
  const [memberFilter, setMemberFilter] = useState(DASHBOARD_MEMBER_FILTERS.ALL);
  const [searchQuery, setSearchQuery] = useState("");
  // The Generation dropdown: the generations ticked in the open panel, the
  // ones in force, and whether the panel is open. Ticks only take hold on
  // Apply, as on the State dropdown; none in force lists every generation.
  const [showGenerationPanel, setShowGenerationPanel] = useState(false);
  const [pendingGenerations, setPendingGenerations] = useState([]);
  const [appliedGenerations, setAppliedGenerations] = useState([]);
  const generationFilterRef = useRef(null);
  const [reportLoading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [report, setReport] = useState({ totalCount: 0, rows: [] });
  // The family lines the footer key and the head-of-line names are built from.
  // The report endpoint names a row's colour but not who heads that line, so
  // the lines come off the same statistics payload the other detail pages read.
  const [legendLines, setLegendLines] = useState([]);
  // The same roster, by member id: the report endpoint sends neither the
  // deceased status, the in-law flag nor the multiple-birth fields, so the
  // markers beside a name are read off that member's roster record.
  const [rosterById, setRosterById] = useState(() => new Map());
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  // The phone's footer key comes from the roster, a separate and slower call. A
  // report opened ahead of it showed a short legend that grew moments later, so
  // the phone holds the page for it; a failed roster call still lets it open.
  const loading = reportLoading || (isCompactLayout && rosterLoading);

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // Close the Generation panel on an outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (
        generationFilterRef.current &&
        !generationFilterRef.current.contains(e.target)
      ) {
        setShowGenerationPanel(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setReport(await fetchFamilyInAgeOrder(getFamilyTreeIdFromStorage()));
      } catch (error) {
        console.error("Error fetching family in age order:", error);
        setReport({ totalCount: 0, rows: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  // Settled on its own: a call that failed leaves the report itself on screen
  // with only the head-of-line names and the name markers missing. Fetched on
  // every layout, since the wide one names the head on each row and the phone
  // one names it in the footer key.
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode !== 200 || !response?.data) return;

        const members = extractAllMembersFromStatisticsResponse(response.data);
        setRosterById(
          new Map(
            members
              .map((member) => [String(member?.id ?? member?.member_id ?? ""), member])
              .filter(([id]) => id)
          )
        );
        setLegendLines(
          buildDashboardColorLegendLines(
            members,
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              members
            )
          )
        );
      } catch (error) {
        console.error("Error fetching family roster:", error);
      } finally {
        setRosterLoading(false);
      }
    };

    fetchRoster();
  }, []);

  /**
   * The record a row's markers and its member tab are read from: its roster
   * record where the roster knows it, else the report's own row — which
   * carries none of those fields, so it prints a bare name and reads as a
   * living descendant, which is all the endpoint returns.
   */
  const getMarkerMember = useCallback(
    (row) =>
      rosterById.get(row.id) ?? {
        ...row.member,
        present_or_deceased: row.member?.present_or_deceased ?? 1,
      },
    [rosterById]
  );

  /**
   * The report rows with each living member's age today, read off the date
   * of birth on the roster record.
   * Where the roster has no date, or the member is deceased, the endpoint's
   * own age stands.
   */
  const reportRows = useMemo(
    () =>
      report.rows.map((row) => {
        const record = getMarkerMember(row);
        if (isDashboardDeceasedMember(record)) return row;

        const currentAge = getMemberCurrentAge(record);
        return currentAge === null ? row : { ...row, age: currentAge };
      }),
    [report.rows, getMarkerMember]
  );

  // A member tab picks who is listed and clears the sort side, so the tab
  // stands selected on its own and the list falls into that tab's own order.
  const toggleMemberFilter = (filter) =>
    setMemberFilter(
      applyDashboardMemberFilterToggle(memberFilter, filter, setSortBy)
    );

  /**
   * Who is listed. The shared rule runs on the roster records, and the rows
   * are matched back by identity, so this page tabs exactly as every other
   * detail page does.
   */
  const filteredRows = useMemo(() => {
    const tabRows = (() => {
      if (memberFilter === DASHBOARD_MEMBER_FILTERS.ALL) return reportRows;

      const rowByRecord = new Map(
        reportRows.map((row) => [getMarkerMember(row), row])
      );

      return filterDashboardMembersByMemberFilter(
        [...rowByRecord.keys()],
        memberFilter
      ).map((record) => rowByRecord.get(record));
    })();

    // The Generation dropdown narrows the tab's rows to the generations
    // ticked, then the search box narrows them again, on the name as printed.
    const generationRows = appliedGenerations.length
      ? tabRows.filter((row) => appliedGenerations.includes(row.generation))
      : tabRows;

    return filterDashboardMembersBySearch(
      generationRows,
      searchQuery,
      (row) => row.sortName,
      getMarkerMember
    );
  }, [reportRows, getMarkerMember, memberFilter, appliedGenerations, searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  /** Every generation the report lists, lowest first — the dropdown's options. */
  const generationOptions = useMemo(
    () =>
      [
        ...new Set(
          report.rows
            .map((row) => row.generation)
            .filter((generation) => generation !== null && generation !== undefined)
        ),
      ].sort((a, b) => a - b),
    [report.rows]
  );

  const openGenerationPanel = () => {
    // The panel reopens on the generations in force, not on ticks left
    // unapplied last time.
    setPendingGenerations(appliedGenerations);
    setShowGenerationPanel((prev) => !prev);
  };

  const togglePendingGeneration = (generation) =>
    setPendingGenerations((prev) =>
      prev.includes(generation)
        ? prev.filter((value) => value !== generation)
        : [...prev, generation].sort((a, b) => a - b)
    );

  // Applying or clearing generations drops any lit sort, so the list lands in
  // its opening order: generation, line colour and age once one is picked.
  const applyGenerations = () => {
    setAppliedGenerations(pendingGenerations);
    setSortBy("none");
    setShowGenerationPanel(false);
  };

  const clearGenerations = () => {
    setPendingGenerations([]);
    setAppliedGenerations([]);
    setSortBy("none");
    setShowGenerationPanel(false);
  };

  // "2, 3" or "1, 2 +2" — what the trigger reads while generations are applied.
  const generationFilterValue = formatFilterValueList(
    appliedGenerations.map(String)
  );

  /**
   * Head of line per colour, from the lines the footer key is built from — so
   * the name printed beside a colour on a row is spelled exactly as that key
   * spells it. Keyed on colour + hex, with a colour-only fallback for rows whose
   * hex is spelled differently from the heads-of-line record's.
   */
  const headNameByLine = useMemo(() => {
    const byColorAndHex = new Map();
    const byColor = new Map();

    legendLines.forEach(({ headName, colorName, hexCode }) => {
      if (!headName) return;
      byColorAndHex.set(familyLineKey(colorName, hexCode), headName);
      const colorOnly = familyLineKey(colorName, "");
      if (!byColor.has(colorOnly)) byColor.set(colorOnly, headName);
    });

    return { byColorAndHex, byColor };
  }, [legendLines]);

  const getLineHeadName = (colorName, hexCode) =>
    headNameByLine.byColorAndHex.get(familyLineKey(colorName, hexCode)) ||
    headNameByLine.byColor.get(familyLineKey(colorName, "")) ||
    "";

  /**
   * The order the list reads in. With no sort lit — which is where a member tab
   * leaves the bar — the tab's own order stands, falling back to oldest first,
   * the one this report opens in.
   *
   * Only the Gen sort groups by generation; Age, A-Z and Line Color each order
   * the whole report as one list, so Age reads straight from the oldest member
   * in the family to the youngest rather than restarting at every generation.
   *
   * Sorting is stable, so rows the endpoint already ordered keep that order
   * wherever a comparison ties.
   */
  // With generations applied, a tab that would otherwise open oldest first
  // opens by generation, line colour and age instead.
  const baseSort = getDashboardBaseSortForMemberFilter(memberFilter, DEFAULT_SORT);
  const unsortedSort =
    appliedGenerations.length > 0 && baseSort === DEFAULT_SORT
      ? GENERATION_DEFAULT_SORT
      : baseSort;
  const effectiveSort = sortBy === "none" ? unsortedSort : sortBy;

  const sortedRows = useMemo(() => {
    // Members of the same age are read by date of birth — month and day, and
    // the year where the birthday has yet to come round — so two 15-year-olds
    // sit in age order rather than by name: the earlier birth date is the
    // older member, so it leads when the list reads oldest first and trails
    // when it reads youngest first. The report row carries only the age; the
    // date comes off the roster record. Unknown dates sort last either way.
    const compareDob = (a, b, { oldestFirst }) => {
      const left = getMemberDobTimestamp(getMarkerMember(a));
      const right = getMemberDobTimestamp(getMarkerMember(b));
      if (left === right) return 0;
      if (left === -1) return 1;
      if (right === -1) return -1;
      return oldestFirst ? left - right : right - left;
    };
    const byAge = (descending) => (a, b) =>
      compareNumbers(a.age, b.age, { descending }) ||
      compareDob(a, b, { oldestFirst: descending }) ||
      a.sortName.localeCompare(b.sortName);

    const byAgeOldestFirst = byAge(true);
    // Unknown date of death sorts last, as an unknown age does.
    const compareDeath = (a, b) => {
      const left = getMemberDeathTimestamp(getMarkerMember(a));
      const right = getMemberDeathTimestamp(getMarkerMember(b));
      if (left === right) return 0;
      if (left === -1) return 1;
      if (right === -1) return -1;
      return left - right;
    };
    const sorted = [...filteredRows];

    switch (effectiveSort) {
      case "youngest":
        return sorted.sort(byAge(false));
      // The deceased tabs are read by date of death, so members who died in the
      // same year sit together.
      case "death-asc":
        return sorted.sort(
          (a, b) => compareDeath(a, b) || a.sortName.localeCompare(b.sortName)
        );
      case "alpha-asc":
        return sorted.sort(
          (a, b) => a.sortName.localeCompare(b.sortName) || byAgeOldestFirst(a, b)
        );
      case "alpha-desc":
        return sorted.sort(
          (a, b) => b.sortName.localeCompare(a.sortName) || byAgeOldestFirst(a, b)
        );
      case "color":
        return sorted.sort(
          (a, b) =>
            a.colorName.localeCompare(b.colorName) ||
            a.sortName.localeCompare(b.sortName) ||
            byAgeOldestFirst(a, b)
        );
      case GENERATION_DEFAULT_SORT:
        return sorted.sort(
          (a, b) =>
            compareNumbers(a.generation, b.generation) ||
            a.colorName.localeCompare(b.colorName) ||
            byAgeOldestFirst(a, b)
        );
      // Oldest, the order the report opens in: age runs across the whole
      // report, generation and all — an unknown age sorts last rather than
      // leading the list.
      default:
        return sorted.sort(byAgeOldestFirst);
    }
  }, [filteredRows, getMarkerMember, effectiveSort]);

  /**
   * Generation dropdown, drawn as Age Range is on Age Groups: a trigger that
   * leads the bar's row and a panel of tick boxes, one per generation the
   * report lists, with Clear and Apply under them.
   */
  const generationDropdown = (
    <div
      ref={generationFilterRef}
      className="selctions-option position-relative desc-age-gen-filter"
    >
      <div
        className={filterTriggerClass(
          "selection-drop desc-age-gen-filter-trigger",
          Boolean(generationFilterValue)
        )}
        style={{ fontWeight: "bold" }}
        onClick={(e) => {
          e.stopPropagation();
          openGenerationPanel();
        }}
      >
        <FilterTriggerLabel
          label={generationFilterValue ? "Gen" : "Generation"}
          value={generationFilterValue}
        />
      </div>
      {showGenerationPanel && (
        <div
          className="member-list desc-age-gen-filter-list"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="desc-age-gen-filter-options">
            {generationOptions.map((generation) => (
              <label key={generation} className="desc-age-gen-filter-option">
                <input
                  type="checkbox"
                  checked={pendingGenerations.includes(generation)}
                  onChange={() => togglePendingGeneration(generation)}
                />
                <span>Generation {generation}</span>
              </label>
            ))}
          </div>
          <div className="btn-container">
            <button type="button" className="btn btn-clear" onClick={clearGenerations}>
              Clear
            </button>
            <button type="button" className="btn btn-apply" onClick={applyGenerations}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderRow = (row) => {
    // The markers after the name, by the rules Total Family Members prints
    // them: the asterisk for an in-law who does not start a line, the angel for
    // the deceased, the Twin/Trip/Quad tag for a multiple birth.
    const markerMember = getMarkerMember(row);
    const showInLawAsterisk =
      isDashboardInLawMember(markerMember) &&
      !isExplicitDashboardStartOrTopOfLineMember(markerMember);
    const multipleBirthSup = getMultipleBirthSuperscriptText(markerMember);
    // Deceased only: a living member's row leaves the column blank rather
    // than printing a dash down the whole list.
    const isDeceased = isDashboardDeceasedMember(markerMember);
    const ageAtDeath = isDeceased
      ? getMemberAgeAtDeathLabel(markerMember)
      : null;
    // A child not yet a year old prints the months — "5 mos." — or, within
    // the first month, the days, rather than a bare 0.
    const ageLabel = getMemberAgeLabel(markerMember, row.age);

    return (
      <React.Fragment key={row.id}>
        <div className="desc-age-gen-cell desc-age-gen-gen">
          {row.generation === null ? MISSING_VALUE : row.generation}
        </div>
        <div className="desc-age-gen-cell desc-age-gen-age">
          {ageLabel === null ? MISSING_VALUE : ageLabel}
        </div>
        <div className="desc-age-gen-cell desc-age-gen-age desc-age-gen-death-age">
          {!isDeceased ? "" : ageAtDeath === null ? MISSING_VALUE : ageAtDeath}
        </div>
        <div className="desc-age-gen-cell desc-age-gen-name">
          <DashboardNameMarkerGroup>
            <DashboardPhotoName
              {...getDashboardMemberNamePhotoUrls(row.member)}
              onViewPhoto={openPhoto}
            >
              <DashboardMemberNameLabel
                baseName={row.baseName}
                descendantSpouseName={row.descendantSpouseName}
                afterBaseName={
                  showInLawAsterisk && row.descendantSpouseName ? (
                    <DashboardInLawAsterisk />
                  ) : null
                }
              />
            </DashboardPhotoName>
            {showInLawAsterisk && !row.descendantSpouseName && (
              <DashboardInLawAsterisk />
            )}
            {isDeceased && <DashboardDeceasedAngel />}
            {multipleBirthSup && (
              <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
            )}
          </DashboardNameMarkerGroup>
        </div>
        {/* The wide layout has room to name who heads the line right here, so
            it does — and carries no family-line key in its footer. The phone
            layout prints the colour alone and leaves the heads to that key. */}
        <div className="desc-age-gen-cell desc-age-gen-line">
          <DashboardLineColorName
            colorName={row.colorName}
            hexCode={row.hexCode}
            headName={
              isCompactLayout ? "" : getLineHeadName(row.colorName, row.hexCode)
            }
          />
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className="dashboard-container desc-in-age-generation-page dashboard-detail-page">
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <h3 className="dashboard-detail-title">
              Generations &amp; Ages
            </h3>
            {/* The count follows the member tab: on All it is the endpoint's
                own total — the same number the dashboard card shows — and on
                any other tab however many people that tab is listing. */}
            <div className="dashboard-detail-count">
              {memberFilter === DASHBOARD_MEMBER_FILTERS.ALL &&
              !isSearching &&
              appliedGenerations.length === 0
                ? report.totalCount
                : filteredRows.length}
            </div>
            {!loading && report.rows.length > 0 && (
              /* The search and the filter row share one block, so the search
                 centres over the row under it. The block sits at the left
                 edge, under the heading, rather than slid to the report's
                 centre. */
              <div className="desc-age-gen-controls">
                <DashboardDetailSearchBox
                  value={searchQuery}
                  onChange={setSearchQuery}
                  centreOverDivider={false}
                />
                {/* The Generation dropdown leads the bar's row, outside the bar
                    itself, which clips anything that opens past its edge. The
                    bar is the shared report tab set — Start/Top of Line,
                    Descendants, then Living and Deceased. */}
                <div className="desc-age-gen-filter-row">
                  {generationDropdown}
                  <DashboardDetailSortFilterBar
                    {...DASHBOARD_REPORT_MEMBER_FILTER_BAR_PROPS}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    memberFilter={memberFilter}
                    onMemberFilterToggle={toggleMemberFilter}
                    defaultSortByMemberFilter={{
                      [memberFilter]: unsortedSort,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading descendants...</p>
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="desc-in-age-generation-page dashboard-detail-content-wrapper">
            <div className="desc-age-gen-list dashboard-detail-list">
              {/* Column titles. Each one is a grid child like the cells under
                  it, so a title wider than its entries widens that track and
                  the column stays centred beneath its own label. The two text
                  columns carry their own column class here as well, which is
                  what makes their titles read flush left with the entries
                  instead of centred over them. They stick to the top of the
                  scroller, so the titles stay on screen however far down the
                  report is read. */}
              <div className="desc-age-gen-cell desc-age-gen-col-title">Gen</div>
              <div className="desc-age-gen-cell desc-age-gen-col-title desc-age-gen-age-title">Age</div>
              <div className="desc-age-gen-cell desc-age-gen-col-title desc-age-gen-death-age-title">
                Age at Death
              </div>
              <div className="desc-age-gen-cell desc-age-gen-name desc-age-gen-col-title">
                Name
              </div>
              <div className="desc-age-gen-cell desc-age-gen-line desc-age-gen-col-title">
                {/* The phone layout prints the colour alone; the wide one names
                    who heads the line beside it. */}
                {isCompactLayout ? "Line Color" : "Head of Line/Color"}
              </div>
              {sortedRows.map(renderRow)}
            </div>

            <div className="desc-in-age-generation-page in-law-footer">
              {/* The multiple-birth tags the key lists are the ones on the rows
                  above it, as on Total Family Members. */}
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(
                  sortedRows.map(getMarkerMember)
                )}
              />
              {/* Family lines are a phone-only key, as on the sibling reports:
                  the wide layout has room to name a line on the row itself. */}
              {isCompactLayout && <DashboardColorLegend lines={legendLines} />}
            </div>
          </div>
        )}
      </div>

      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
    </div>
  );
}

export default DescInAgeGeneration;
